import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { Search, ArrowRight, CheckCircle2, Circle, Mail, Loader2, Video } from "lucide-react";

export default function AdminStudents() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("all");
  const [gradeFilter, setGradeFilter] = useState("all");
  const [sendingId, setSendingId] = useState(null);
  const [sendingEarlyBirdId, setSendingEarlyBirdId] = useState(null);
  const [sendingFeedbackId, setSendingFeedbackId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/students").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const schools = useMemo(() => ["all", ...Array.from(new Set(items.map((s) => s.school_name).filter(Boolean))).sort()], [items]);
  const grades = useMemo(() => ["all", ...Array.from(new Set(items.map((s) => s.grade).filter(Boolean))).sort()], [items]);

  const filtered = items.filter((s) => {
    if (schoolFilter !== "all" && s.school_name !== schoolFilter) return false;
    if (gradeFilter !== "all" && String(s.grade || "") !== gradeFilter) return false;
    if (!q) return true;
    const hay = `${s.name} ${s.email} ${s.phone || ""} ${s.school_name || ""} ${s.top_career || ""}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  });

  const sendSampleReport = async (student) => {
    setSendingId(student.id);
    try {
      await api.post(`/admin/students/${student.id}/send-sample-report`);
      toast.success(`Sample report emailed to ${student.email}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't send that email");
    } finally {
      setSendingId(null);
    }
  };

  const sendEarlyBirdOffer = async (student) => {
    setSendingEarlyBirdId(student.id);
    try {
      await api.post(`/admin/students/${student.id}/send-early-bird-offer`);
      toast.success(`Early bird offer emailed to ${student.email}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't send that email");
    } finally {
      setSendingEarlyBirdId(null);
    }
  };

  const sendFeedbackVideoRequest = async (student) => {
    setSendingFeedbackId(student.id);
    try {
      const r = await api.post("/admin/feedback-videos/request", { email: student.email });
      if (r.data.email_status === "sent") {
        toast.success(`Feedback video request emailed to ${student.email}`);
      } else if (r.data.email_status === "not_configured") {
        toast.error("Email isn't configured on the server yet.");
      } else {
        toast.error("Couldn't send that email");
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't send that email");
    } finally {
      setSendingFeedbackId(null);
    }
  };

  const FUNNEL_STAGE = {
    never_paid: { label: "Stuck: Never paid", className: "bg-red-100 text-red-800" },
    started_unpaid: { label: "Stuck: Started, unpaid", className: "bg-orange-100 text-orange-800" },
    paid_not_started: { label: "Paid, not started", className: "bg-blue-100 text-blue-800" },
    mid_assessment: { label: "Stuck: Mid-assessment", className: "bg-orange-100 text-orange-800" },
    completed: { label: "Completed", className: "bg-green-100 text-green-800" },
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <span className="label-mono">Admin</span>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter mt-2">Student Directory</h1>
        <p className="text-[#52525B] mt-2">Every registered student across all schools and universities.</p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" strokeWidth={2.5} />
            <input
              data-testid="admin-students-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, school…"
              className="pl-10 pr-4 py-2 border-2 border-[#0A0A0A] rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[260px]"
            />
          </div>
          <select value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)} className="px-3 py-2 border-2 border-[#0A0A0A] rounded-full text-sm bg-white">
            {schools.map((s) => <option key={s} value={s}>{s === "all" ? "All schools" : s}</option>)}
          </select>
          <select value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} className="px-3 py-2 border-2 border-[#0A0A0A] rounded-full text-sm bg-white">
            {grades.map((g) => <option key={g} value={g}>{g === "all" ? "All grades" : `Grade ${g}`}</option>)}
          </select>
        </div>

        <div className="mt-6 label-mono">Total: {filtered.length}</div>

        <div className="mt-4 card-brutal overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FEF08A] border-b-2 border-[#0A0A0A]">
              <tr>
                <th className="text-left px-4 py-3 label-mono">Student</th>
                <th className="text-left px-4 py-3 label-mono">Phone</th>
                <th className="text-left px-4 py-3 label-mono">School</th>
                <th className="text-left px-4 py-3 label-mono">Class · Board</th>
                <th className="text-left px-4 py-3 label-mono">Registered</th>
                <th className="text-left px-4 py-3 label-mono">Paid</th>
                <th className="text-left px-4 py-3 label-mono">Stuck At</th>
                <th className="text-left px-4 py-3 label-mono">Top Career</th>
                <th className="text-left px-4 py-3 label-mono">Report</th>
                <th className="text-right px-4 py-3 label-mono">Nurture Email</th>
                <th className="text-right px-4 py-3 label-mono">Early Bird Email</th>
                <th className="text-right px-4 py-3 label-mono">Share Feedback</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="11" className="px-4 py-6 label-mono">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="11" data-testid="admin-students-empty" className="px-4 py-6 text-[#52525B]">No students match.</td></tr>
              ) : (
                filtered.map((s) => (
                  <tr key={s.id} data-testid={`admin-student-${s.id}`} className="border-b border-[#0A0A0A]/10 hover:bg-[#FAFAF9]">
                    <td className="px-4 py-3">
                      <div className="font-semibold">{s.name}</div>
                      <div className="text-xs text-[#52525B]">{s.email}</div>
                    </td>
                    <td className="px-4 py-3">{s.phone || <span className="text-[#52525B]">—</span>}</td>
                    <td className="px-4 py-3">{s.school_name || "—"}</td>
                    <td className="px-4 py-3">{s.grade || "—"} · {s.education_board || "—"}</td>
                    <td className="px-4 py-3 text-[#52525B]">{s.created_at ? format(new Date(s.created_at), "dd MMM yyyy, h:mm a") : "—"}</td>
                    <td className="px-4 py-3">
                      {s.has_paid ? (
                        <span className="inline-flex items-center gap-1 text-green-700 font-semibold"><CheckCircle2 size={14} strokeWidth={2.5} /> Paid</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[#52525B]"><Circle size={14} strokeWidth={2.5} /> Unpaid</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const stage = FUNNEL_STAGE[s.funnel_stage] || FUNNEL_STAGE.never_paid;
                        return (
                          <span data-testid={`funnel-stage-${s.id}`} className={`inline-block text-xs font-semibold px-2 py-1 rounded-full ${stage.className}`}>
                            {stage.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">{s.top_career || <span className="text-[#52525B]">—</span>}</td>
                    <td className="px-4 py-3">
                      {s.latest_result_id ? (
                        <Link to={`/report/${s.latest_result_id}`} className="inline-flex items-center gap-1 font-semibold">
                          View <ArrowRight size={14} strokeWidth={2.5} />
                        </Link>
                      ) : <span className="text-xs text-[#52525B]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {s.sample_report_sent_at ? (
                        <div className="text-xs text-[#52525B]">
                          Sent {format(new Date(s.sample_report_sent_at), "dd MMM")}
                          <button
                            onClick={() => sendSampleReport(s)}
                            disabled={sendingId === s.id}
                            data-testid={`resend-sample-report-${s.id}`}
                            className="block ml-auto mt-1 font-semibold underline"
                          >
                            Resend
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => sendSampleReport(s)}
                          disabled={sendingId === s.id}
                          data-testid={`send-sample-report-${s.id}`}
                          className="btn-brutal bg-white px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
                        >
                          {sendingId === s.id ? <Loader2 className="animate-spin" size={12} /> : <Mail size={12} strokeWidth={2.5} />}
                          Send Sample
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {s.early_bird_sent_at ? (
                        <div className="text-xs text-[#52525B]">
                          Sent {format(new Date(s.early_bird_sent_at), "dd MMM")}
                          <button
                            onClick={() => sendEarlyBirdOffer(s)}
                            disabled={sendingEarlyBirdId === s.id}
                            data-testid={`resend-early-bird-${s.id}`}
                            className="block ml-auto mt-1 font-semibold underline"
                          >
                            Resend
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => sendEarlyBirdOffer(s)}
                          disabled={sendingEarlyBirdId === s.id}
                          data-testid={`send-early-bird-${s.id}`}
                          className="btn-brutal bg-yellow-300 px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
                        >
                          {sendingEarlyBirdId === s.id ? <Loader2 className="animate-spin" size={12} /> : <Mail size={12} strokeWidth={2.5} />}
                          Send Offer
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {s.feedback_video_requested_at ? (
                        <div className="text-xs text-[#52525B]">
                          Sent {format(new Date(s.feedback_video_requested_at), "dd MMM")}
                          <button
                            onClick={() => sendFeedbackVideoRequest(s)}
                            disabled={sendingFeedbackId === s.id}
                            data-testid={`resend-feedback-video-request-${s.id}`}
                            className="block ml-auto mt-1 font-semibold underline"
                          >
                            Resend
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => sendFeedbackVideoRequest(s)}
                          disabled={sendingFeedbackId === s.id}
                          data-testid={`send-feedback-video-request-${s.id}`}
                          className="btn-brutal bg-white px-3 py-1.5 text-xs inline-flex items-center gap-1.5"
                        >
                          {sendingFeedbackId === s.id ? <Loader2 className="animate-spin" size={12} /> : <Video size={12} strokeWidth={2.5} />}
                          Share Feedback
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
